import { handleActions } from "redux-actions";
import {
  FETCH_CURRENT_USER_SUCCESS,
  FETCH_FILTERS_SUCCESS,
  FETCH_TILES_SUCCESS,
  FETCH_CHART_SUCCESS,
  UPDATE_DATES,
  UPDATE_CUSTOM_RANGE,
  UPDATE_TABLE_VIEW_SELECTED,
  SET_DATES,
  SET_FILTERS_DIRTY,
  SET_LOADING,
  SET_UPDATES_LOADING,
  RESET_FILTER_SELECTED,
  UPDATE_CLASS_TYPES,
  UPDATE_PERIOD_TYPE,
  UPDATE_CHART_TYPE_SELECTED
} from "../sagas/averageClassSizeByInstructorSagas";
import {
  getDateRanges,
  getRangeOptions,
  getRangeOptionsWithCustom,
  getSelectedRangeOptionWithCustom
} from "../../../../../../utils/date-range-options";

function selectCategoriesByChecked(categories) {
  if (categories.length && categories[0].checked !== undefined) {
    return categories.filter((category) => category.checked);
  }
  return categories;
}

const classTypePermissions = {
  Instructors: "hasPermissionForChartByInstructor",
  Days: "hasPermissionForChartByDay",
  Times: "hasPermissionForChartByTime"
};

const chartTypeOptions = [
  {
    id: 0,
    name: "Average Class Size by Instructor",
    paramName: "pages/attendance/average_class_size_per_instructor_chart",
    classTypeName: "Instructors",
    tileTitle: {
      totalAttendance: "Total Attendance for All Instructors",
      visits: "Total Visits Taught from Teachers Selected",
      totalAttendancePercent: "% of Attendance Taught by Teachers Selected"
    }
  },
  {
    id: 1,
    name: "Class Attendance By Day",
    paramName: "pages/attendance/number_of_class_attendance_by_day_chart",
    classTypeName: "Days",
    tileTitle: {
      totalAttendance: "Total Attendance",
      visits: "Number Of Visits",
      totalAttendancePercent: "% of Total Attendance"
    }
  },
  {
    id: 2,
    name: "Class Attendance By Time",
    paramName: "pages/attendance/number_of_class_attendance_by_time_chart",
    classTypeName: "Times",
    tileTitle: {
      totalAttendance: "Total Attendance",
      visits: "Number Of Visits",
      totalAttendancePercent: "% of Total Attendance"
    }
  }
];

const defaultChartType = {
  id: 0,
  name: "Average Class Size by Instructor",
  paramName: "pages/attendance/average_class_size_per_instructor_chart",
  classTypeName: "Instructors",
  tileTitle: {
    totalAttendance: "Total Attendance for All Instructors",
    visits: "Total Visits Taught from Teachers Selected",
    totalAttendancePercent: "% of Attendance Taught by Teachers Selected"
  }
};

function filterOptionsByPermissions(options, permissions) {
  const filteredOptions = [];

  for (const option of options) {
    const permissionKey = classTypePermissions[option.classTypeName];

    if (permissionKey && permissions[permissionKey]) {
      filteredOptions.push(option);
    }
  }

  return filteredOptions;
}

export const initialState = {
  isLoading: true,
  updatesLoading: false,
  filtersDirty: false,
  currentUser: {},
  chartData: {},
  tilesData: {},
  selectedRequestDates: {},
  filters: {},
  selectedFilters: {
    period_type: "Monthly",
    table_view: false,
    categories: []
  },
  chartTypes: {
    all: [],
    selected: defaultChartType
  },
  periodTypeOptions: ["Weekly", "Monthly"],
  selectedRangeOption: {},
  rangeOptions: []
};

export default handleActions(
  {
    [FETCH_CURRENT_USER_SUCCESS]: (state, action) => {
      const filteredOptions = filterOptionsByPermissions(
        chartTypeOptions,
        action.payload.permissions
      );
      return {
        ...state,
        currentUser: action.payload,
        chartTypes: {
          ...state.chartTypes,
          all: filterOptionsByPermissions(chartTypeOptions, action.payload.permissions),
          selected: filteredOptions.length > 0 ? filteredOptions[0] : defaultChartType
        }
      };
    },
    [FETCH_FILTERS_SUCCESS]: (state, action) => ({
      ...state,
      filters: action.payload,
      selectedFilters: {
        ...state.selectedFilters,
        categories: selectCategoriesByChecked(action.payload.classTypes.options)
      }
    }),
    [FETCH_TILES_SUCCESS]: (state, action) => ({
      ...state,
      tilesData: action.payload.tiles
    }),
    [FETCH_CHART_SUCCESS]: (state, action) => ({
      ...state,
      chartData: action.payload
    }),
    [SET_LOADING]: (state, action) => ({
      ...state,
      isLoading: action.payload
    }),
    [SET_UPDATES_LOADING]: (state, action) => ({
      ...state,
      updatesLoading: action.payload
    }),
    [SET_FILTERS_DIRTY]: (state, action) => ({
      ...state,
      filtersDirty: action.payload
    }),
    [SET_DATES]: (state, action) => {
      const rangeOptions = getRangeOptions(
        action.payload.endDate,
        state.currentUser.calendarStartDate
      );

      return {
        ...state,
        selectedRequestDates: action.payload,
        rangeOptions: getRangeOptionsWithCustom(rangeOptions, action),
        selectedRangeOption: getSelectedRangeOptionWithCustom(action)
      };
    },
    [UPDATE_CUSTOM_RANGE]: (state, action) => ({
      ...state,
      selectedRangeOption: action.payload,
      selectedRequestDates: getDateRanges(
        state.currentUser.calendarEndDate,
        action.payload,
        state.currentUser.calendarStartDate
      ),
      filtersDirty: true
    }),
    [UPDATE_CHART_TYPE_SELECTED]: (state, action) => ({
      ...state,
      chartTypes: {
        ...state.chartTypes,
        selected: action.payload
      }
    }),
    [UPDATE_PERIOD_TYPE]: (state, action) => ({
      ...state,
      filtersDirty: true,
      selectedFilters: {
        ...state.selectedFilters,
        period_type: action.payload
      }
    }),
    [UPDATE_CLASS_TYPES]: (state, action) => {
      return {
        ...state,
        filtersDirty: true,
        selectedFilters: {
          ...state.selectedFilters,
          categories: action.payload
        }
      };
    },
    [UPDATE_DATES]: (state, action) => ({
      ...state,
      selectedRequestDates: action.payload || state.selectedRequestDates,
      filtersDirty: true,
      rangeOptions: getRangeOptionsWithCustom(state.rangeOptions, action),
      selectedRangeOption: getSelectedRangeOptionWithCustom(action),
      selectedFilters: {
        ...state.selectedFilters
      }
    }),

    [UPDATE_TABLE_VIEW_SELECTED]: (state, action) => ({
      ...state,
      selectedFilters: {
        ...state.selectedFilters,
        table_view: action.payload
      }
    }),
    [RESET_FILTER_SELECTED]: (state) => ({
      ...state,
      filtersDirty: false,
      selectedFilters: {
        ...initialState.selectedFilters
      },
      chartTypes: {
        ...state.chartTypes,
        selected: defaultChartType
      }
    })
  },
  initialState
);
